/**
 * @graph-stack cortex-admin
 * @graph-domain Marketing
 * @graph-business SNSコンテンツ生成画面の編集行（テーマ・タイトル）のstate操作をまとめるフック
 * @graph-connects none
 */
import { useState } from 'react';
import type {
  ConfirmedThemeTitle,
  GeneratedThemeTitle,
} from '../../../../hooks/useSnsContentGenerator.js';

/**
 * 編集画面の1行。生成された行と手動追加された行の両方をこの1つの形で表す。
 *
 * 見た目上のUI構造は生成行・手動行で分岐させない（どちらも最終的に「テーマ＋タイトル」という
 * 同じ形になるため）。
 */
export interface ThemeTitleRow {
  /** 行の識別子。React key用にidで安定させる */
  id: string;
  /** 投稿テーマ */
  theme: string;
  /** 投稿タイトル */
  title: string;
  /** この行を個別または一括で実行（画像生成対象への追加）済みか */
  isExecuted: boolean;
}

/** 行の識別子を発行する。FilterableColumnsEditorのgenerateRowIdと同じ方式 @graph-connects none */
function generateRowId(): string {
  return Math.random().toString(36).substring(2, 11);
}

/** 「テーマ・タイトルを追加」クリック時に追加する空の手動行を1件生成する。 @graph-connects none */
export function createEmptyThemeTitleRow(): ThemeTitleRow {
  return {
    id: generateRowId(),
    theme: '',
    title: '',
    isExecuted: false,
  };
}

/** モック生成結果1件から編集行を1件生成する。 @graph-connects none */
export function createGeneratedThemeTitleRow(item: GeneratedThemeTitle): ThemeTitleRow {
  return {
    id: generateRowId(),
    theme: item.theme,
    title: item.title,
    isExecuted: false,
  };
}

/**
 * テーマ・タイトル入力の自由記述テキストを正規化する。テキストエリアでの折り返し入力を許容する
 * ため、改行を含む複数行の値が入りうる。生の改行が画像プロンプトやTikTokのタイトルに混ざると
 * 困るため、連続する空白（改行を含む）を半角スペース1個にまとめてから前後をtrimする。
 *
 * @graph-connects none
 */
export function normalizeThemeTitleText(value: string): string {
  return value.replace(/\s+/g, ' ').trim();
}

/**
 * 1行を正規化したうえで確定形式に変換する。テーマ・タイトルのどちらかが空になる行はnullを返す。
 *
 * @graph-connects none
 */
export function toConfirmedThemeTitle(row: ThemeTitleRow): ConfirmedThemeTitle | null {
  const theme = normalizeThemeTitleText(row.theme);
  const title = normalizeThemeTitleText(row.title);
  if (theme === '' || title === '') return null;
  return { id: row.id, theme, title };
}

/**
 * 複数行から確定リストを組み立てる（一括実行用）。
 *
 * テーマ・タイトルの前後空白を取り除き、どちらかが空になった行は除外する。
 * テーマが重複する行は、先に現れた行だけを残す。
 *
 * @graph-connects none
 */
export function toConfirmedThemeTitles(rows: ThemeTitleRow[]): ConfirmedThemeTitle[] {
  const seenThemes = new Set<string>();
  const confirmed: ConfirmedThemeTitle[] = [];
  for (const row of rows) {
    const item = toConfirmedThemeTitle(row);
    if (item === null) continue;
    if (seenThemes.has(item.theme)) continue;
    seenThemes.add(item.theme);
    confirmed.push(item);
  }
  return confirmed;
}

/**
 * SNSコンテンツ生成画面の編集行の state と操作をまとめて返す。
 *
 * @graph-business 編集画面の行の追加・削除・更新・生成結果の追記・実行済みマークを管理する
 * @graph-connects none
 */
export function useThemeTitleRows() {
  const [rows, setRows] = useState<ThemeTitleRow[]>([]);

  const addManualRow = (): void => {
    setRows((prev) => [...prev, createEmptyThemeTitleRow()]);
  };

  const removeRow = (id: string): void => {
    setRows((prev) => prev.filter((row) => row.id !== id));
  };

  const updateTheme = (id: string, theme: string): void => {
    setRows((prev) => prev.map((row) => (row.id === id ? { ...row, theme } : row)));
  };

  const updateTitle = (id: string, title: string): void => {
    setRows((prev) => prev.map((row) => (row.id === id ? { ...row, title } : row)));
  };

  /** 生成結果を末尾に追記する。既存行は消さない（複数回クリックで累積させるため） */
  const appendGenerated = (items: GeneratedThemeTitle[]): void => {
    setRows((prev) => [...prev, ...items.map(createGeneratedThemeTitleRow)]);
  };

  /** 指定した1行だけを実行済みにする（行ごとの個別実行ボタン用） */
  const markRowExecuted = (id: string): void => {
    setRows((prev) => prev.map((row) => (row.id === id ? { ...row, isExecuted: true } : row)));
  };

  /** 指定した複数行をまとめて実行済みにする（一括実行ボタン用） */
  const markRowsExecuted = (ids: string[]): void => {
    const idSet = new Set(ids);
    setRows((prev) => prev.map((row) => (idSet.has(row.id) ? { ...row, isExecuted: true } : row)));
  };

  return {
    rows,
    addManualRow,
    removeRow,
    updateTheme,
    updateTitle,
    appendGenerated,
    markRowExecuted,
    markRowsExecuted,
  };
}
